import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $downloadStatementStore, $initialCurrenciesAvailableStore, $statementsStore } from '@lib/store';
import { useListState } from '@shared/hooks';
import { cn, PaletteUtils, PermissionUtils } from '@shared/utils';
import { type StatementsResponse, StatementType } from '@shared/api';
import { ConsolidateTable } from '../statementsConsolidateTable';
import { AccountTable } from '../statementsAccountTable';
import { CardTable } from '../statementsCardTable';
import { Select, Tabs } from '@shared/widgets';

interface StatementsCardTabProps {
  className?: string;
}

export const StatementsCardTab = (props: StatementsCardTabProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const statementsStore = useUnit($statementsStore.store);
  const downloadStatementStore = useUnit($downloadStatementStore.store);

  const [tab, setTab] = useState<StatementType>();
  const [currency, setCurrency] = useState<string>();

  const statementsConsolidatedState = useListState({ list: 'statementsConsolidated' });
  const statementsAccountState = useListState({ list: 'statementsAccount' });
  const statementsCardState = useListState({ list: 'statementsCard' });
  const stateMap = {
    [StatementType.Consolidated]: statementsConsolidatedState,
    [StatementType.Account]: statementsAccountState,
    [StatementType.Card]: statementsCardState,
  };

  const { t } = useTranslation('statements');

  useEffect(() => {
    if (PermissionUtils.viewStatementsConsolidatedTab()) setTab(StatementType.Consolidated);
    else if (PermissionUtils.viewStatementsAccountTab()) setTab(StatementType.Account);
    else if (PermissionUtils.viewStatementsCardTab()) setTab(StatementType.Card);

    return () => $statementsStore.events.clear();
  }, []);

  useEffect(() => {
    if (!tab) return;
    $statementsStore.events.getStatements({ StatementType: tab, Currency: currency, FromIndex: stateMap[tab].fromIndex, Count: stateMap[tab].count });
  }, [currency, tab, statementsConsolidatedState, statementsAccountState, statementsCardState]);

  const downloadHandler = (statement: StatementsResponse) => {
    const dateParts = statement.date.split('-');
    $downloadStatementStore.events.downloadStatement({
      id: statement.statementId,
      statementType: statement.statementType,
      year: dateParts[0],
      monthNumber: dateParts[1],
      relationshipNumber: statement.relationshipNumber,
    });
  };

  const tabs = useMemo(
    () =>
      [
        PermissionUtils.viewStatementsConsolidatedTab() && { label: t('card.tabs.consolidated'), value: StatementType.Consolidated },
        PermissionUtils.viewStatementsAccountTab() && { label: t('card.tabs.account'), value: StatementType.Account },
        PermissionUtils.viewStatementsCardTab() && { label: t('card.tabs.card'), value: StatementType.Card },
      ].filter(Boolean) as { label: string; value: StatementType }[],
    [t],
  );
  return (
    <div
      className={cn(PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black', props.className)}
    >
      <div className={cn('flex flex-nowrap items-center justify-between', 'max-lg:flex-col-reverse')}>
        <Tabs
          className='w-full'
          listClassName={cn(PaletteUtils.isInsigniaPalette && 'gap-0', PaletteUtils.isVcfcPalette && 'gap-12')}
          labelClassName={cn(PaletteUtils.isInsigniaPalette && 'pt-4 px-6 pb-7 bg-white px-6 dark:bg-soft-black')}
          options={tabs}
          value={tab}
          onChange={setTab}
        />
        <div className={cn('flex items-center justify-end pb-6', 'max-lg:ml-auto max-lg:w-full')}>
          <Select
            className={cn('min-w-[160px]', 'max-lg:min-w-full')}
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
            value={currency}
            onChange={setCurrency}
          />
        </div>
      </div>
      {tab === StatementType.Consolidated && PermissionUtils.viewStatementsConsolidatedTab() && (
        <ConsolidateTable
          isLoading={statementsStore.loading}
          error={statementsStore.error}
          onRefresh={() =>
            $statementsStore.events.getStatements({
              StatementType: tab,
              Currency: currency,
              FromIndex: stateMap[tab].fromIndex,
              Count: stateMap[tab].count,
            })
          }
          statements={statementsStore.data.data}
          onDownloadStatement={downloadHandler}
          downloadingIds={downloadStatementStore.loadingIds}
          totalItemCount={statementsStore.data.total}
          currentPage={statementsConsolidatedState.page}
          countItemPage={statementsConsolidatedState.count}
          onChangePage={statementsConsolidatedState.onChangePage}
          onChangeCountItemPage={statementsConsolidatedState.onChangeCount}
        />
      )}
      {tab === StatementType.Account && PermissionUtils.viewStatementsAccountTab() && (
        <AccountTable
          isLoading={statementsStore.loading}
          error={statementsStore.error}
          onRefresh={() =>
            $statementsStore.events.getStatements({
              StatementType: tab,
              Currency: currency,
              FromIndex: stateMap[tab].fromIndex,
              Count: stateMap[tab].count,
            })
          }
          statements={statementsStore.data.data}
          onDownloadStatement={downloadHandler}
          downloadingIds={downloadStatementStore.loadingIds}
          totalItemCount={statementsStore.data.total}
          currentPage={statementsAccountState.page}
          countItemPage={statementsAccountState.count}
          onChangePage={statementsAccountState.onChangePage}
          onChangeCountItemPage={statementsAccountState.onChangeCount}
        />
      )}
      {tab === StatementType.Card && PermissionUtils.viewStatementsCardTab() && (
        <CardTable
          isLoading={statementsStore.loading}
          error={statementsStore.error}
          onRefresh={() =>
            $statementsStore.events.getStatements({
              StatementType: tab,
              Currency: currency,
              FromIndex: stateMap[tab].fromIndex,
              Count: stateMap[tab].count,
            })
          }
          statements={statementsStore.data.data}
          onDownloadStatement={downloadHandler}
          downloadingIds={downloadStatementStore.loadingIds}
          totalItemCount={statementsStore.data.total}
          currentPage={statementsCardState.page}
          countItemPage={statementsCardState.count}
          onChangePage={statementsCardState.onChangePage}
          onChangeCountItemPage={statementsCardState.onChangeCount}
        />
      )}
    </div>
  );
};
